/**
 * How the chat turns a file id into bytes it can show. The content panel
 * passes one built on `useThreadFile`; without one, attachments render as
 * name-only tiles and a sent frame shows no thumbnail.
 */
import type { MessageAttachment } from '@discovr/types';

/** One file's bytes (and a `blob:` URL for them), and how fetching them is going. */
export interface FileSourceResult {
  url?: string;
  blob?: Blob;
  isLoading: boolean;
  isError: boolean;
  /** Fetches the file again, e.g. after a failed load. */
  retry(): void;
}

export interface FileRequestOptions {
  /** False holds the fetch — e.g. while a thumbnail is off screen. Defaults to true. */
  enabled?: boolean;
}

export interface FileSource {
  /**
   * A hook. The chat calls it once, unconditionally, at the top of each small
   * component that shows a file. `NO_FILE_ID` means there's nothing to fetch,
   * and the source stays idle for it.
   */
  useFile(fileId: string, options?: FileRequestOptions): FileSourceResult;
  /** The name the viewer's Download saves a file under. */
  downloadName(attachment: MessageAttachment): string;
}

/** Nothing to fetch: a local preview is showing, or the file has no uploaded id yet. */
export const NO_FILE_ID = '';

/** The id to fetch for a file — none while its local preview is showing. */
export function fetchableFileId(fileId: string | undefined, localUrl: string | undefined): string {
  if (localUrl) return NO_FILE_ID;
  return fileId ?? NO_FILE_ID;
}
